import type { EquipmentManual } from '@/types';

// 注塑成型车间主要设备操作手册库
// 覆盖 12 件关键设备：
//   IMM-101 / IMM-102 / HOP-201 / DRY-201 / MOLD-201 / HEAT-301 / CHILL-301 / CTRL-401
//   v3 新增：ROB-201 / CONV-201 / CONV-202 / MTC-301
// 内容贴合塑料制品厂注塑车间实际工艺，AI 师傅老吴讲评时可引用
export const injectionManuals: Record<string, EquipmentManual> = {
  'IMM-101': {
    overview:
      '立式注塑机 IMM-101 是车间主力机型，常用于嵌件成型（带金属嵌件、磁铁、五金件的制品）。' +
      '采用立式锁模、卧式注射结构，方便上下嵌件。最大锁模力 200 吨，注射量 500 g。',
    operatingProcedure: [
      '上电后等待加热筒升温至工艺温度（240°C 左右），开机前必须等温 30 分钟使各段温度均匀',
      '空打 2-3 模检查熔体颜色、流动性是否正常，无降解、无杂质',
      '装入嵌件，确认嵌件就位且预热（避免冷嵌件造成熔接痕）',
      '点动合模 → 注射 → 保压 → 冷却 → 开模 → 顶出，先低压低速验证',
      '工艺参数稳定后切自动模式，记录首件并检验',
      '每 2 小时抽检一次重量偏差与外观，发现波动及时调整',
      '换料时先用新料推空老料，至少推 10 模无杂色再算正式生产',
    ],
    safetyNotes: [
      '加料口热！料筒温度 220-280°C，禁止徒手接触',
      '开模前必须确认无人在模具区域，配防夹光幕',
      '熔体压力高达 100+ MPa，禁止对喷嘴方向站人',
      '嵌件温度低于料温过多会出现包封不良，必要时预热到 60°C 以上',
      '紧急停机后必须等熔体压力释放、温度降下来再开机筒检查',
    ],
    troubleshooting: [
      { symptom: '短射、制品缺料', action: '提高注射压力到 85 MPa+；检查止逆阀是否磨损；确认料温到位' },
      { symptom: '飞边毛刺', action: '检查锁模力是否到 1200 kN；降低注射压力；检查模具间隙' },
      { symptom: '制品翘曲', action: '检查模具温度均匀性；提高保压时间；冷却水流量加大' },
      { symptom: '制品出现银纹', action: '物料含水率超标，提高干燥温度延长干燥时间' },
      { symptom: '螺杆打滑（计量不稳）', action: '检查背压阀、料口温度、料粒尺寸；适当降低螺杆转速' },
    ],
  },

  'IMM-102': {
    overview:
      '卧式注塑机 IMM-102 适合大批量薄壁件与精密件生产，水平注射、水平锁模，自动化嫁接方便。' +
      '最大锁模力 250 吨，注射量 800 g，配伺服泵节能。',
    operatingProcedure: [
      '开机前确认料斗有干燥料，含水率 < 400 ppm',
      '加热筒分段升温：喂料段 200°C / 压缩段 225°C / 计量段 240°C / 喷嘴 245°C',
      '等温后空打清料 → 校核注射压力 / 速度 / 保压三段曲线',
      '切自动后首检三模，确认重量偏差 ±0.3 g 之内',
      '运行中每 4 小时记录一次工艺参数和制品合格率',
      '收工前清理喷嘴和料筒残料，避免下次开机料黑',
    ],
    safetyNotes: [
      '伺服泵切机时禁止突切电源，要按操作面板的待机键',
      '更换模具时先排空熔体并冷却到 100°C 以下',
      '锁模力大，模具拆装必须使用专用吊具',
      '夜间停机要拉合模到中位，避免长时间锁紧',
    ],
    troubleshooting: [
      { symptom: '周期时间延长', action: '检查模具温度（>80°C 冷却不下来），加大冷却水流量' },
      { symptom: '熔体喷射纹', action: '降低注射速度初段，或提高模温让熔体流动平稳' },
      { symptom: '重量飘忽 ±0.6g+', action: '检查计量是否稳定、背压是否合适、止逆阀磨损' },
      { symptom: '制品发黑', action: '料筒过热导致降解；检查热电偶，把计量段降到 235°C' },
    ],
  },

  'HOP-201': {
    overview:
      '原料料斗 HOP-201 储存待加工塑料颗粒，配气体加热与磁性物分离功能。容积 50 kg，可观察料位。',
    operatingProcedure: [
      '加料前确认物料牌号、批号、含水率符合工艺要求',
      '料斗内壁要干净，禁止混入异色或异种料',
      '料位保持 30-90%，过低易堵料口，过高散热不好',
      '使用前打开磁分离器开关，过滤金属杂质',
      '加完料合盖，避免空气湿气进入',
    ],
    safetyNotes: [
      '加料时戴防滑手套，颗粒易滑',
      '勿在料斗顶站立或攀爬，防止跌入',
      '高温料斗（带加热）要小心烫伤',
    ],
    troubleshooting: [
      { symptom: '料口堵塞、不下料', action: '检查料斗内是否结块、磁铁是否吸住、料斗加热是否过热' },
      { symptom: '料位指示不准', action: '清洁料位传感器探头，或更换' },
    ],
  },

  'DRY-201': {
    overview:
      '物料干燥机 DRY-201 通过热风循环 + 除湿轮去除塑料颗粒水分，常用于 PA / PC / PET / ABS 等吸湿性塑料。' +
      '设定温度 75-95°C，干燥时间 3-6 小时。',
    operatingProcedure: [
      '加料前看物料 MSDS，确认推荐干燥温度和时间',
      '空载预热 15 分钟，干燥仓内温度达到 setpoint',
      '加入物料，料层厚度不超过料斗 80%',
      '观察露点和回风温度，记录干燥进度',
      '干燥完成后含水率应 < 400 ppm（PA/PC < 200 ppm）',
      '从干燥机直接加到注塑机料斗，避免冷却后再吸湿',
    ],
    safetyNotes: [
      '机壳烫，戴防烫手套',
      '高温下严禁打开干燥仓盖，热风喷出会烫伤',
      '加热丝维护要先断电冷却至 50°C 以下',
    ],
    troubleshooting: [
      { symptom: '含水率不下降', action: '检查除湿轮是否失效、加热丝是否烧断、风量是否足够' },
      { symptom: '料发黄变色', action: '干燥温度过高或时间过长，根据料牌号下调' },
    ],
  },

  'MOLD-201': {
    overview:
      '模具 MOLD-201 是制品成型的最终决定因素，含浇注系统、冷却水道、顶出机构。模具温度由冷水机循环控制。' +
      '正常模温 45-75°C，工程塑料类需 80-120°C。',
    operatingProcedure: [
      '开机前打开模具冷却水阀，确保水路通畅',
      '点动合模 → 缓慢加压 → 检查分型面有无溢料',
      '生产前先打 3-5 模废件，观察分型面、顶出、外观是否正常',
      '每班记录合模次数（寿命管理）',
      '收工前打开模具吹干内部水汽，喷防锈油',
      '换模时先卸熔体 → 释压 → 冷却到 50°C 再拆',
    ],
    safetyNotes: [
      '禁止在合模时把手伸入型腔，配光幕保护',
      '顶出杆动作时禁止接近顶针板',
      '模具吊装重量大，必须用专用吊具和挂耳',
      '冷却水管接头要锁紧，热模喷水会烫伤',
    ],
    troubleshooting: [
      { symptom: '模具温度持续超 80°C', action: '检查冷却水流量、水温、水管是否堵塞' },
      { symptom: '制品脱模困难（粘模）', action: '降低模温、检查脱模剂、调整顶出力' },
      { symptom: '飞边持续出现', action: '检查锁模力、模具分型面、模板平行度' },
      { symptom: '顶针顶白或顶出顶不到位', action: '检查顶针润滑、顶出行程、顶出力是否到位' },
    ],
  },

  'HEAT-301': {
    overview:
      '加热筒 HEAT-301 通过电阻丝分段加热（喂料 / 压缩 / 计量 / 喷嘴），把塑料颗粒塑化成熔体。' +
      '温度精度直接决定制品质量与寿命。常用工艺塑料 220-260°C；高温尼龙 280-310°C。',
    operatingProcedure: [
      '开机前检查热电偶接线、加热环固定、温控仪显示',
      '冷态启动时各段温度逐段升温，避免热应力',
      '升温节奏：喂料段先低 → 计量段拉高，避免堵料',
      '到温后等温 30 分钟使熔体均匀',
      '生产中每段温度波动应 ≤ ±3°C',
      '换料/停机后清空料筒，避免老料降解',
    ],
    safetyNotes: [
      '加热环表面 300°C 以上，禁止徒手触摸',
      '热电偶维护要先停机断电',
      '料筒堵料禁止用蛮力推螺杆，先升温熔化',
      '清理喷嘴时戴防烫面罩，熔体飞溅烫伤严重',
    ],
    troubleshooting: [
      { symptom: '某段温度异常（高/低）', action: '检查热电偶、加热环、固态继电器、温控仪' },
      { symptom: '熔体变色发黑', action: '料筒过热导致降解；降温清料；检查温控精度' },
      { symptom: '熔体气泡', action: '料未干 + 料筒过热使水气化；检查干燥机和温控' },
      { symptom: '料筒发抖（剪切热失控）', action: '降低螺杆转速、提高背压、清理止逆阀' },
    ],
  },

  'CHILL-301': {
    overview:
      '冷水机 CHILL-301 提供模具冷却循环水，控制模温稳定。冷却水温 7-25°C，流量 14-24 L/min。' +
      '是注塑周期与制品收缩率的关键设备。',
    operatingProcedure: [
      '开机前检查水箱水位（>80%）和清洁度',
      '启动循环泵 5 分钟后再启动制冷压缩机',
      '设定出水温度通常比模具设定温度低 8-15°C',
      '观察进出水温差，温差大说明带走热量多',
      '定期清洗水箱过滤器（建议 1 次/周）',
      '冬季停机要排空管路防冻',
    ],
    safetyNotes: [
      '压缩机维护需专业制冷技工，含 R134a 等制冷剂',
      '禁止在压缩机运转时切断水路（容易爆裂）',
      '水管接头要锁紧，高压水喷出会伤人',
    ],
    troubleshooting: [
      { symptom: '出水温度上不来', action: '检查制冷剂量、冷凝器风扇、水箱水温' },
      { symptom: '流量不足 < 10 L/min', action: '清洗过滤器、检查循环泵叶轮、补水' },
      { symptom: '机器过载', action: '可能水温过高、制冷剂不足或压缩机故障，停机检查' },
    ],
  },

  'CTRL-401': {
    overview:
      '注塑总控柜 CTRL-401 集中管理两台注塑机、加热筒、冷水机和料斗的控制信号，' +
      '含 PLC、伺服驱动器、安全继电器、通讯总线。',
    operatingProcedure: [
      '每日开机前查看故障历史和报警',
      '检查 380V 三相电压稳定（375-385 V）',
      '通讯指示灯绿色 = 正常，红色或闪烁需排查',
      '系统负载 30-70% 为健康，超过 80% 要检查程序卡顿',
      '维护需断电挂牌',
    ],
    safetyNotes: [
      '柜内 380V 高压，非电工严禁开柜门',
      '维护必须挂"禁止合闸"警示牌',
      '夏季注意通风，柜内温度 > 45°C 易死机',
    ],
    troubleshooting: [
      { symptom: '通讯中断、设备失联', action: '检查网线接头、PLC 状态灯、终端电阻' },
      { symptom: '主电压偏低 < 370V', action: '联系厂区配电班，可能 UPS 或变压器问题' },
      { symptom: '柜内温度过高', action: '清理散热风扇灰尘，检查空调或通风' },
    ],
  },

  // ============================================================
  // v3 新增：4 台新设备手册
  // ============================================================
  'ROB-201': {
    overview:
      '取件机械手 ROB-201 是六轴伺服机械手，装在两台注塑机之间，' +
      '负责模具开模后从型腔取出制品并放到成品输送带。配真空吸盘 + 力矩传感器，' +
      '抓取节拍 4-8 秒/件，与模具开合精确同步。',
    operatingProcedure: [
      '开机前手动回零，确认六个轴位置正常',
      '校准吸盘真空度（建议 -85 ~ -90 kPa），漏气率检测合格',
      '示教抓取轨迹：模具开 → 进入型腔 → 抓取 → 退出 → 放下 → 复位',
      '与模具开合信号联动，必须收到"开模到位"才进入型腔',
      '生产中每 2 小时检查一次取件成功率，<97% 要排查',
      '记录每班抓取次数，到 50 万次大保养',
      '换模时手动模式回到安全位，避免新模具碰撞',
    ],
    safetyNotes: [
      '抓取动作区禁止靠近，配光幕保护',
      '吸盘漏气会让制品高空跌落，注意脚下安全',
      '示教模式下速度倍率 ≤ 30%',
      '紧急停机后须重新回零方可继续生产',
      '六轴机械手关节力矩大，调试时严禁手部进入工作区',
    ],
    troubleshooting: [
      { symptom: '取件成功率 <97%', action: '检查吸盘磨损、真空管路漏气、模具顶出力是否到位' },
      { symptom: '吸盘真空度上不去 (>-60 kPa)', action: '更换吸盘密封圈或检查真空泵' },
      { symptom: '机械手撞模', action: '立即急停！检查"开模到位"信号、复位精度' },
      { symptom: '机械手卡顿/速度慢', action: '检查伺服电机温度、润滑、编码器反馈' },
      { symptom: '抓取后掉件', action: '真空保压不足、吸盘选型不对、或抓取角度偏' },
    ],
  },

  'CONV-201': {
    overview:
      '上料输送带 CONV-201 是料斗到干燥机之间的颗粒输送装置，' +
      '采用 PE 食品级带 + 不锈钢导槽。带速 0.5-1.2 m/min，上料速率 8-18 kg/h。' +
      '配电机过载保护和颗粒堵料检测光电传感器。',
    operatingProcedure: [
      '开机前清理传送带表面，确认无残留杂物',
      '检查带子张紧度，过松会打滑，过紧损伤电机',
      '设定带速：常规牌号 0.8 m/min，密度高的料降到 0.6',
      '上料速率应与下游消耗匹配（料斗料位维持 50-80%）',
      '运行中观察是否有颗粒卡边、跑偏',
      '每天班前用压缩空气吹一遍轨道，避免粉尘累积',
    ],
    safetyNotes: [
      '运行中禁止伸手进入传送带，配紧急拉绳',
      '电机驱动器侧有高压，维修必须断电',
      '颗粒堆积超过料槽顶部时立即停机',
    ],
    troubleshooting: [
      { symptom: '带速跌至 0.2 m/min', action: '清理卡料、检查电机过载、张紧轮卡死' },
      { symptom: '上料速率不足', action: '调高带速或检查料斗出口是否堵' },
      { symptom: '颗粒跑偏掉地', action: '调整带子对中、检查导槽是否变形' },
      { symptom: '电机异响发烫', action: '可能过载或缺润滑，停机检修' },
    ],
  },

  'CONV-202': {
    overview:
      '成品输送带 CONV-202 是模具到检测仪/下料区之间的成品输送装置，' +
      '采用网带式结构便于产品冷却散热。带速 0.4-1.0 m/min。配在途计件传感器，' +
      '与机械手放件动作同步。',
    operatingProcedure: [
      '开机前清理带面，确认上次生产残留已清空',
      '设定带速以匹配下游检测节拍（INST-201 检测 + 包装能力）',
      '运行中关注在途制品数 ≤ 30 件，过多说明下游堵',
      '检测到产品堆积报警立即停机查清原因',
      '换产品时清空带面 + 校准计件传感器',
    ],
    safetyNotes: [
      '热制品刚出模可能 50°C+，戴手套作业',
      '禁止跨越运行中的输送带',
      '与机械手联动调试要确认时序无碰撞',
    ],
    troubleshooting: [
      { symptom: '带速降至 0.1', action: '驱动电机故障或张紧轮卡死，停机检查' },
      { symptom: '在途数超 30', action: '下游检测仪或包装堵了，先清理再恢复' },
      { symptom: '制品掉落带外', action: '带速过快或工件不平稳，降速 + 加挡边' },
    ],
  },

  'MTC-301': {
    overview:
      '模温机 MTC-301 是给模具提供热水循环以维持精密模温的设备，' +
      '与冷水机分工：冷水机走低温冷却（<25°C），模温机走中高温（45-120°C）。' +
      '工程塑料如 PC/PA 必须用模温机。配双通道独立控温。',
    operatingProcedure: [
      '开机前检查水箱液位（80%以上）和水路阀门状态',
      '设定温度后等待 5-10 分钟达到稳态，不能急于生产',
      '生产中实际温度应稳定在设定 ±2°C 之内',
      '循环流量保持 10-16 L/min，过低则模温不稳',
      '换模时停模温机 + 排空模具水道防生锈',
      '夏季注意散热，冬季注意防冻',
      '每月清洗水箱过滤网',
    ],
    safetyNotes: [
      '模温机出水 80°C+ 高温，水管接头要锁紧，禁止热运转拆接头',
      '加热罐体烫，戴防烫手套',
      '维护必须停机降温到 40°C 以下',
      '禁止使用普通自来水（结垢严重），用软水或纯水',
    ],
    troubleshooting: [
      { symptom: '实际温度上不去', action: '检查加热器、温控仪、循环泵' },
      { symptom: '循环流量 <8 L/min', action: '清洗过滤器、检查泵叶轮、补水' },
      { symptom: '模具温度跟不上设定', action: '可能模温机循环不足或模具水道堵' },
      { symptom: '机器过热报警', action: '过滤器堵塞或冷却散热不良' },
      { symptom: '出水有铁锈/异味', action: '水质问题，清洗水箱并更换软水' },
    ],
  },
};
